import { asLocale, selectCurrentUser } from '@saniplus/api-client';
import type { Locale } from '@saniplus/contracts';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '../../app/hooks';
import { useTaskGate } from '../tasks';
import { HomeScreen } from './HomeScreen';
import { RecoveryScreen } from './RecoveryScreen';
import { usePunchScreen } from './hooks/usePunchScreen';

/**
 * W3 / W8 — the route.
 *
 * One screen, two faces: the home screen when the product is working, and the
 * recovery screen the moment a punch is refused, queued or gated. Both live at
 * the same path on purpose — a refusal that navigates somewhere else is a
 * refusal a worker can lose by tapping "back".
 *
 * The shell (`AppShellWorker`) is the app's, not this feature's: the rail, the
 * connectivity band and the bottom nav render around this. See the feature
 * README for the two props the shell needs from here.
 */
export interface PunchPageProps {
  /** Where « Voir mes quarts » goes once W4 exists. */
  readonly shiftsPath?: string;
}

export function PunchPage({ shiftsPath }: PunchPageProps = {}) {
  const { i18n } = useTranslation('punch');
  const user = useAppSelector(selectCurrentUser);
  const locale: Locale = asLocale(i18n.language) ?? 'fr';

  const screen = usePunchScreen();
  const taskGate = useTaskGate();

  if (screen.recovery !== null) {
    return (
      <RecoveryScreen
        recovery={screen.recovery}
        context={screen.context}
        queue={screen.queue}
        now={screen.now}
        locale={locale}
        onDismiss={screen.dismissRecovery}
        onPunchAnyway={screen.punchAnyway}
        onRequestLocation={screen.requestLocation}
        onRetry={screen.dismissRecovery}
        {...(shiftsPath === undefined ? {} : { shiftsPath })}
        {...(user === null ? {} : { employeeName: `${user.firstName} ${user.lastName}` })}
        {...(user === null ? {} : { employeeNumber: user.employeeNumber })}
      />
    );
  }

  return (
    <HomeScreen
      home={screen.home}
      context={screen.context}
      queue={screen.queue}
      loading={screen.loading}
      busy={screen.busy}
      now={screen.now}
      locale={locale}
      confirmedDeviceAt={screen.confirmedDeviceAt}
      onPunchIn={screen.punchIn}
      onPunchOut={screen.punchOut}
      tasks={taskGate.tasks}
      onOverridePunchOut={taskGate.overridePunchOut}
      {...(shiftsPath === undefined ? {} : { shiftsPath })}
    />
  );
}

export default PunchPage;
